import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Loans.css';

const Loans = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payingUser, setPayingUser] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    lenderName: '',
    isActive: true,
    originalBalance: 0,
    interestRate: 0
  });
  const [isUpdatingLoan, setIsUpdatingLoan] = useState(false);
  const [updateLoanStatus, setUpdateLoanStatus] = useState(null);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [newPaymentAmount, setNewPaymentAmount] = useState('');
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [updatePaymentStatus, setUpdatePaymentStatus] = useState(null);
  const [expandedPayments, setExpandedPayments] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.selectedUser) {
      const u = location.state.selectedUser;

      const formatted = {
        id: u.id || Math.random().toString(),
        userId: u.userId,
        name: location.state.name || "",
        email: location.state.email || "",
        totalLoan: Number(u.originalBalance || 0),
        amountPay: Number(u.amountPay || 0),
        paymentHistory: u.paymentHistory || [],
        lenderName: u.lenderName || '',
        loanType: u.loanType || '',
        originationDate: u.originationDate || '',
        interestRate: u.interestRate || 0,
        loanTerm: u.loanTerm || 0,
        accountNumber: u.accountNumber || '',
        nickName: u.nickName || '',
        nameOnAccount: u.nameOnAccount || '',
        loans: [u]
      };
      setSelectedUser(formatted);
    }
  }, [location.state]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handlePayAmount = async () => {
    if (!payAmount || isNaN(payAmount) || Number(payAmount) <= 0) {
      setPaymentStatus({ type: 'error', message: 'Please enter a valid amount.' });
      return;
    }

    const amount = Number(payAmount);
    const loanId = payingUser.id;

    const totalPaid = payingUser.amountPay || 0;
    const remaining = payingUser.totalLoan - totalPaid;

    if (amount > remaining) {
      setPaymentStatus({ type: 'error', message: `Amount cannot exceed the remaining balance of $${remaining.toFixed(2)}` });
      return;
    }

    setIsPaying(true);
    setPaymentStatus(null);

    try {
      await api.post(`/admin-management/loans/${loanId}/pay`, {
        amount: amount
      });

      setPaymentStatus({ type: 'success', message: 'Payment recorded successfully!' });

      // Update local state to reflect payment immediately
      setUsers(prev => prev.map(u => {
        if (u.id === loanId) {
          const updatedAmountPaid = (u.amountPay || 0) + amount;
          return { ...u, amountPay: updatedAmountPaid };
        }
        return u;
      }));

      if (selectedUser?.id === loanId) {
        setSelectedUser(prev => ({
          ...prev,
          amountPay: (prev.amountPay || 0) + amount,
          // We'd ideally need the new history item too, but fetchLoans will handle it eventually
        }));
      }

      fetchLoans();

      setTimeout(() => {
        setPayAmount('');
        setShowPayModal(false);
        setPayingUser(null);
        setPaymentStatus(null);
        setIsPaying(false);
      }, 1500);

    } catch (err) {
      console.error('Error paying loan:', err);
      setPaymentStatus({
        type: 'error',
        message: err.response?.data?.message || err.response?.data?.error || 'Failed to process payment'
      });
      setIsPaying(false);
    }
  };

  const handleUpdateLoan = async () => {
    if (!editData.lenderName || isNaN(editData.originalBalance) || isNaN(editData.interestRate)) {
      setUpdateLoanStatus({ type: 'error', message: 'Please fill all fields correctly.' });
      return;
    }

    setIsUpdatingLoan(true);
    setUpdateLoanStatus(null);

    try {
      const res = await api.post(`/admin-management/loans/${selectedUser.id}`, {
        lenderName: editData.lenderName,
        isActive: editData.isActive,
        originalBalance: Number(editData.originalBalance),
        interestRate: Number(editData.interestRate)
      });

      const updatedLoan = res.data?.loan;
      setUpdateLoanStatus({ type: 'success', message: 'Loan details updated successfully!' });

      // Update local state immediately with response data
      if (updatedLoan) {
        const formatted = {
          id: updatedLoan.id || selectedUser.id,
          userId: updatedLoan.userId,
          name: selectedUser.name,
          email: selectedUser.email,
          totalLoan: Number(updatedLoan.originalBalance || 0),
          amountPay: Number(updatedLoan.amountPay || 0),
          paymentHistory: updatedLoan.paymentHistory || selectedUser.paymentHistory,
          lenderName: updatedLoan.lenderName || '',
          loanType: updatedLoan.loanType || '',
          originationDate: updatedLoan.originationDate || '',
          interestRate: updatedLoan.interestRate || 0,
          loanTerm: updatedLoan.loanTerm || 0,
          accountNumber: updatedLoan.accountNumber || '',
          nickName: updatedLoan.nickName || '',
          nameOnAccount: updatedLoan.nameOnAccount || '',
          isActive: updatedLoan.isActive,
          loans: [updatedLoan]
        };
        setSelectedUser(formatted);
        setUsers(prev => prev.map(u => u.id === selectedUser.id ? formatted : u));
      }

      fetchLoans();

      setTimeout(() => {
        setShowEditModal(false);
        setUpdateLoanStatus(null);
        setIsUpdatingLoan(false);
      }, 1500);

    } catch (err) {
      setUpdateLoanStatus({
        type: 'error',
        message: err.response?.data?.message || 'Failed to update loan details'
      });
      setIsUpdatingLoan(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!newPaymentAmount || isNaN(newPaymentAmount) || Number(newPaymentAmount) <= 0) {
      setUpdatePaymentStatus({ type: 'error', message: 'Please enter a valid amount.' });
      return;
    }

    setIsUpdatingPayment(true);
    setUpdatePaymentStatus(null);

    try {
      await api.post(`/admin-management/payment-history/${editingPayment.id}`, {
        amount: Number(newPaymentAmount)
      });

      setUpdatePaymentStatus({ type: 'success', message: 'Payment updated successfully!' });

      fetchLoans();

      setTimeout(() => {
        setShowEditPaymentModal(false);
        setUpdatePaymentStatus(null);
        setIsUpdatingPayment(false);
      }, 1500);

    } catch (err) {
      setUpdatePaymentStatus({
        type: 'error',
        message: err.response?.data?.message || 'Failed to update payment'
      });
      setIsUpdatingPayment(false);
    }
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      const res = await api.get('/admin-management/loans');
      const apiLoans = res.data?.loans || [];

      const formattedLoans = apiLoans.map(loan => ({
        id: loan.id || Math.random().toString(),
        userId: loan.userId,
        name: `${loan.loanAccountUser?.firstName || ''} ${loan.loanAccountUser?.lastName || ''}`,
        email: loan.loanAccountUser?.email || '',
        totalLoan: Number(loan.originalBalance || 0),
        amountPay: Number(loan.amountPay || 0),
        paymentHistory: loan.paymentHistory || [],
        lenderName: loan.lenderName || '',
        loanType: loan.loanType || '',
        originationDate: loan.originationDate || '',
        interestRate: loan.interestRate || 0,
        loanTerm: loan.loanTerm || 0,
        accountNumber: loan.accountNumber || '',
        nickName: loan.nickName || '',
        nameOnAccount: loan.nameOnAccount || '',
        updateHistory: loan.updateHistory || [],
        loans: [loan]
      }));

      setUsers(formattedLoans);

      // Also update selectedUser if it's currently open to get latest history
      if (selectedUser) {
        const updated = formattedLoans.find(u => u.id === selectedUser.id);
        if (updated) setSelectedUser(updated);
      }

    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.clear();
        window.location.href = '/login';
        return;
      }
      console.error('Loans API error:', err?.response?.data || err.message);
    }
  };

  const UserLoanSummary = () => (
    <div className="card">
      <div className="table-responsive">
        <table className="summary-table">
          <thead>
            <tr>
              <th>User</th>
              <th className="hide-mobile">Loan Type</th>
              <th className="hide-mobile">Original Balance</th>
              <th className="hide-mobile">Interest Rate</th>
              <th>Amount Paid</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const filteredUsers = users.filter(u =>
                u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.email.toLowerCase().includes(searchTerm.toLowerCase())
              );

              if (filteredUsers.length === 0) {
                return (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                      No loans found.
                    </td>
                  </tr>
                );
              }

              return filteredUsers.map((user) => {
                const totalPaid = user.amountPay || 0;
                return (
                  <tr key={user.id} className="clickable-row" onClick={() => setSelectedUser(user)}>
                    <td>
                      <div className="user-info-stack-row">
                        <div className="user-avatar-mini">{user.name?.charAt(0)}</div>
                        <div className="user-meta">
                          <div className="user-name-bold">{user.name}</div>
                          <div className="user-email-gray">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hide-mobile">{user.loanType || 'N/A'}</td>
                    <td className="hide-mobile">{formatCurrency(user.totalLoan)}</td>
                    <td className="hide-mobile">{user.interestRate ? `${user.interestRate}%` : 'N/A'}</td>
                    <td>
                      <span className="amount-highlight">{formatCurrency(totalPaid)}</span>
                    </td>
                    <td>
                      {user.totalLoan - totalPaid <= 0 ? (
                        <span className="status-pill paid">Paid</span>
                      ) : (
                        <span className="status-pill pending">Pending</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <button
                          className="btn-list-action"
                          title="View Details"
                          onClick={(e) => { e.stopPropagation(); setSelectedUser(user); }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                        <button
                          className="btn-list-action"
                          title="Edit Details"
                          onClick={(e) => { e.stopPropagation(); setSelectedUser(user); }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          className={`btn-pay-primary ${user.totalLoan - totalPaid <= 0 ? 'paid-btn' : ''}`}
                          style={{ padding: '6px 16px', fontSize: '12px', marginLeft: '12px' }}
                          disabled={user.totalLoan - totalPaid <= 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPayAmount('');
                            setPaymentStatus(null);
                            setPayingUser(user);
                            setShowPayModal(true);
                          }}
                        >
                          {user.totalLoan - totalPaid <= 0 ? 'Paid' : 'Pay'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );

  const LoanDetail = ({ user }) => {
    const totalPaid = user.amountPay || 0;
    const remaining = user.totalLoan - totalPaid;
    const payments = user.paymentHistory || [];

    return (
      <>
        <div className="loan-detail-view">
          <div className="loan-info-section">
            <h3>Loan Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Original Balance</span>
                <span className="value">{formatCurrency(user.totalLoan)}</span>
              </div>
              <div className="info-item">
                <span className="label">Paid Amount</span>
                <span className="value paid-amount">{formatCurrency(totalPaid)}</span>
              </div>
              <div className="info-item">
                <span className="label">Remaining Balance</span>
                <span className="value">{formatCurrency(remaining)}</span>
              </div>
              <div className="info-item">
                <span className="label">Lender Name</span>
                <span className="value">{user.lenderName || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Loan Type</span>
                <span className="value">{user.loanType || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Origination Date</span>
                <span className="value">
                  {user.originationDate
                    ? new Date(user.originationDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    : 'N/A'}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Interest Rate</span>
                <span className="value">{user.interestRate ? `${user.interestRate}%` : 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Loan Term</span>
                <span className="value">{user.loanTerm ? `${user.loanTerm} months` : 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Account Number</span>
                <span className="value">{user.accountNumber || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Nickname</span>
                <span className="value">{user.nickName || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Name on Account</span>
                <span className="value">{user.nameOnAccount || 'N/A'}</span>
              </div>
            </div>

            <button
              className="btn-pay-primary edit-btn-detail"
              onClick={() => {
                setEditData({
                  lenderName: user.lenderName || '',
                  isActive: user.isActive !== false,
                  originalBalance: user.totalLoan || 0,
                  interestRate: user.interestRate || 0
                });
                setUpdateLoanStatus(null);
                setShowEditModal(true);
              }}
            >
              Edit Details
            </button>
          </div>
        </div>

        <div className="payment-history-section">
          <div className="payment-history-header">
            <h3>Payment History</h3>
            <button
              className={`btn-pay-primary ${remaining <= 0 ? 'paid-btn' : ''}`}
              disabled={remaining <= 0}
              onClick={() => {
                setPayAmount('');
                setPaymentStatus(null);
                setPayingUser(user);
                setShowPayModal(true);
              }}
            >
              {remaining <= 0 ? 'Paid' : 'Pay'}
            </button>
          </div>

          <div className="history-list-v3">
            {[...payments].reverse().map(pay => {
              const hasHistory = user.updateHistory?.some(u => u.changeType === 'PAYMENT_AMOUNT_UPDATE' && u.details?.some(d => d.paymentId === pay.id));

              return (
                <div
                  key={pay.id}
                  className={`payment-history-row ${expandedPayments[pay.id] ? 'is-expanded' : ''} ${hasHistory ? 'has-history' : ''}`}
                  onClick={() => {
                    if (hasHistory) {
                      setExpandedPayments(prev => ({ ...prev, [pay.id]: !prev[pay.id] }));
                    }
                  }}
                >
                  <div className="row-content">
                    <div className="row-title">
                      <span className="main-label">Payment Received</span>
                      <span className="sub-label">
                        {new Date(pay.paymentDate).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>

                    <div className="row-actions">
                      <span className="row-amount">-{formatCurrency(pay.amount)}</span>
                      <div className="action-icons">
                        <button
                          className="btn-minimal-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPayment(pay);
                            setNewPaymentAmount(pay.amount);
                            setUpdatePaymentStatus(null);
                            setShowEditPaymentModal(true);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        {hasHistory && (
                          <div className={`chevron-icon ${expandedPayments[pay.id] ? 'open' : ''}`}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedPayments[pay.id] && (
                    <div className="row-expanded-details">
                      {(user.updateHistory || [])
                        .filter(update => update.changeType === 'PAYMENT_AMOUNT_UPDATE' && update.details?.some(d => d.paymentId === pay.id))
                        .map(update => {
                          const detail = update.details.find(d => d.paymentId === pay.id);
                          if (!detail) return null;

                          return (
                            <div key={update.id} className="history-log-row">
                              <div className="log-info">
                                <span className="old-val">{formatCurrency(detail.old)}</span>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                                  <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                                <span className="new-val">{formatCurrency(detail.new)}</span>
                              </div>
                              <div className="log-meta">
                                {new Date(update.createdAt).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {payments.length === 0 && (
            <div className="no-payments shadow-card" style={{ borderRadius: '16px', border: '1px solid #eee' }}>
              No payment history available.
            </div>
          )}
        </div>

        {user.updateHistory?.some(u => u.changeType === 'LOAN_DETAILS_UPDATE') && (
          <div className="loan-audit-trail" style={{ marginTop: '40px' }}>
            <div className="audit-header">
              <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#1a1d1f', marginBottom: '20px' }}>Activity & Audit Log</h3>
            </div>
            <div className="audit-log-card shadow-card" style={{ background: '#fff', border: '1px solid #eee', borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {user.updateHistory
                .filter(u => u.changeType === 'LOAN_DETAILS_UPDATE')
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map(update => (
                  <div key={update.id} className="audit-entry" style={{ display: 'flex', gap: '20px', paddingBottom: '20px', borderBottom: '1px solid #f4f4f4' }}>
                    <div className="audit-icon-box" style={{ width: '44px', height: '44px', background: '#f4f4f4', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6f767e' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </div>
                    <div className="audit-content" style={{ flex: 1 }}>
                      <div className="title" style={{ fontWeight: '700', color: '#1a1d1f', marginBottom: '6px', fontSize: '15px' }}>Loan Account Updated</div>
                      <div className="change-desc" style={{ fontSize: '14px', color: '#6f767e', lineHeight: '1.6' }}>
                        {update.details.map((d, i) => (
                          <div key={i} style={{ marginBottom: '4px' }}>Modified <strong>{d.field}</strong> from <em style={{ color: '#9ca3af' }}>{d.old}</em> to <strong style={{ color: '#1a1d1f' }}>{d.new}</strong></div>
                        ))}
                      </div>
                      <div className="audit-date" style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px', fontWeight: '500' }}>
                        {new Date(update.createdAt).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="loans-container">
      <div className="finance-header-section loans-header">
        {selectedUser ? (
          <>
            <button
              className="back-pill"
              onClick={() => {
                if (location.state?.from) {
                  navigate(location.state.from);
                } else {
                  setSelectedUser(null);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <span>Back</span>
            </button>

            <div className="header-user-info">
              <div className="user-avatar-mini large">
                {selectedUser.name?.charAt(0)}
              </div>
              <div>
                <h1>{selectedUser.name}</h1>
                <p>Loan Repayment Account</p>
              </div>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            <div className="overview-section">
              <div className="overview-header">
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', fontWeight: '500', margin: 0 }}>Manage and track all user loans</p>
              </div>
            </div>

            <div className="search-container visible-search">
              <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by name or email..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {!selectedUser ? <UserLoanSummary /> : <LoanDetail user={selectedUser} />}

      {showPayModal && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <button className="btn-close-abs" onClick={() => setShowPayModal(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>

            <div className="modal-header-centered">
              <h3>Record Loan Payment</h3>
              <p>Enter the amount to pay for <strong>{payingUser?.name}</strong></p>
            </div>

            <div className="modal-body" style={{ padding: 0 }}>
              {payingUser && (
                <div className="modal-balance-box">
                  <div className="balance-item">
                    <span className="balance-label">TOTAL LOAN</span>
                    <span className="balance-val">{formatCurrency(payingUser.totalLoan || 0)}</span>
                  </div>
                  <div className="balance-item" style={{ textAlign: 'right' }}>
                    <span className="balance-label">REMAINING</span>
                    <span className="balance-val">{formatCurrency((payingUser.totalLoan || 0) - (payingUser.amountPay || 0))}</span>
                  </div>
                </div>
              )}

              <div className="payment-input-image-match">
                <span className="currency-symbol">$</span>
                <input
                  type="number"
                  className="amount-input-match"
                  placeholder="0.00"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  autoFocus
                  disabled={isPaying || paymentStatus?.type === 'success'}
                />
              </div>

              {paymentStatus && (
                <div style={{
                  padding: '12px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  backgroundColor: paymentStatus.type === 'success' ? '#f0fdf4' : '#fef2f2',
                  color: paymentStatus.type === 'success' ? '#10b981' : '#ef4444',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  {paymentStatus.message}
                </div>
              )}

              <div className="modal-actions-v2">
                <button className="btn-cancel-v2" onClick={() => setShowPayModal(false)} disabled={isPaying || paymentStatus?.type === 'success'}>Cancel</button>
                <button
                  className="btn-pay-confirm"
                  onClick={handlePayAmount}
                  disabled={isPaying || paymentStatus?.type === 'success' || ((payingUser?.totalLoan || 0) - (payingUser?.amountPay || 0)) <= 0}
                >
                  {isPaying ? 'Processing...' : 'Pay Amount'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <button className="btn-close-abs" onClick={() => setShowEditModal(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>

            <div className="modal-header-centered">
              <h3>Edit Loan Details</h3>
              <p>Update information for <strong>{selectedUser?.name}</strong></p>
            </div>

            <div className="modal-body" style={{ padding: 0 }}>
              <div className="modal-input-grid">
                <div className="form-group-custom">
                  <label>Lender Name</label>
                  <input
                    type="text"
                    className="input-styled-v2"
                    value={editData.lenderName || ''}
                    onChange={(e) => setEditData({ ...editData, lenderName: e.target.value })}
                  />
                </div>
                {/* <div className="form-group-custom">
                  <label>Service Status</label>
                  <div
                    className={`toggle-group ${editData.isActive ? 'active' : ''}`}
                    onClick={() => setEditData({ ...editData, isActive: !editData.isActive })}
                  >
                    <div className="toggle-switch"></div>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#1a1d1f' }}>
                      {editData.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div> */}
                <div className="form-group-custom">
                  <label>Original Balance ($)</label>
                  <input
                    type="number"
                    className="input-styled-v2"
                    value={editData.originalBalance || 0}
                    onChange={(e) => setEditData({ ...editData, originalBalance: e.target.value })}
                  />
                </div>
                <div className="form-group-custom">
                  <label>Interest Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-styled-v2"
                    value={editData.interestRate || 0}
                    onChange={(e) => setEditData({ ...editData, interestRate: e.target.value })}
                  />
                </div>
              </div>

              {updateLoanStatus && (
                <div style={{
                  padding: '12px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  backgroundColor: updateLoanStatus.type === 'success' ? '#f0fdf4' : '#fef2f2',
                  color: updateLoanStatus.type === 'success' ? '#10b981' : '#ef4444',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  {updateLoanStatus.message}
                </div>
              )}

              <div className="modal-actions-v2">
                <button className="btn-cancel-v2" onClick={() => setShowEditModal(false)} disabled={isUpdatingLoan}>Cancel</button>
                <button
                  className="btn-pay-confirm"
                  onClick={handleUpdateLoan}
                  disabled={isUpdatingLoan || updateLoanStatus?.type === 'success'}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                >
                  {isUpdatingLoan ? <div className="spinner"></div> : 'Update Details'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-content payment-modal">
            <button className="btn-close-abs" onClick={() => setShowEditPaymentModal(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>

            <div className="modal-header-centered">
              <h3>Edit Payment Amount</h3>
              <p>Update transaction details for <strong>{selectedUser?.name}</strong></p>
            </div>

            <div className="modal-body" style={{ padding: 0 }}>
              <div className="form-group-custom" style={{ marginBottom: '24px' }}>
                <label>New Amount ($)</label>
                <div className="payment-input-image-match">
                  <span className="currency-symbol">$</span>
                  <input
                    type="number"
                    className="amount-input-match"
                    placeholder="0.00"
                    value={newPaymentAmount}
                    onChange={(e) => setNewPaymentAmount(e.target.value)}
                    autoFocus
                    disabled={isUpdatingPayment || updatePaymentStatus?.type === 'success'}
                  />
                </div>
              </div>

              {updatePaymentStatus && (
                <div style={{
                  padding: '12px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  backgroundColor: updatePaymentStatus.type === 'success' ? '#f0fdf4' : '#fef2f2',
                  color: updatePaymentStatus.type === 'success' ? '#10b981' : '#ef4444',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  {updatePaymentStatus.message}
                </div>
              )}

              <div className="modal-actions-v2">
                <button className="btn-cancel-v2" onClick={() => setShowEditPaymentModal(false)} disabled={isUpdatingPayment || updatePaymentStatus?.type === 'success'}>Cancel</button>
                <button
                  className="btn-pay-confirm"
                  onClick={handleUpdatePayment}
                  disabled={isUpdatingPayment || updatePaymentStatus?.type === 'success'}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                >
                  {isUpdatingPayment ? <div className="spinner"></div> : 'Update Amount'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Loans;
